import { type Auth0Client, createAuth0Client } from "@auth0/auth0-spa-js";

type AppState = {
    targetUrl?: string;
};

let auth0Client: Auth0Client | null = null;

/**
 * E2E テスト (Playwright) 用の Auth0 client モック。
 * VITE_E2E_AUTH_MOCK=true のときのみ有効。Vite が build 時に評価するため、
 * production ビルドでは分岐に入らず、本物の Auth0 client が必ず使われる。
 *
 * 認証必須ルートを E2E でテストするために isAuthenticated を常に true 返却し、
 * loginWithRedirect / logout は no-op にする。/api/* の mock は Playwright fixture 側で行う。
 */
const e2eAuthMock: Auth0Client | null =
    import.meta.env.VITE_E2E_AUTH_MOCK === "true"
        ? ({
              isAuthenticated: async () => true,
              getUser: async () => ({ sub: "e2e-user", email: "e2e@example.com" }),
              getTokenSilently: async () => "e2e-fake-jwt-token",
              loginWithRedirect: async () => {
                  /* no-op: テストでは redirect させない */
              },
              logout: async () => {
                  /* no-op */
              },
              handleRedirectCallback: async () => ({ appState: { targetUrl: "/" } }),
          } as unknown as Auth0Client)
        : null;

export const getAuth0Instance = async () => {
    if (e2eAuthMock) return e2eAuthMock;
    if (!auth0Client) {
        auth0Client = await createAuth0Client({
            domain: import.meta.env.VITE_AUTH0_DOMAIN,
            clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
            authorizationParams: {
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                redirect_uri: `${window.location.origin}/callback`,
            },
        });
    }
    return auth0Client;
};

// clientLoaderで使用可能な関数群
export const auth0Service = {
    async isAuthenticated() {
        const client = await getAuth0Instance();
        return client.isAuthenticated();
    },

    async getUser() {
        const client = await getAuth0Instance();
        return client.getUser();
    },

    async getToken() {
        const client = await getAuth0Instance();
        return client.getTokenSilently();
    },

    async login() {
        const client = await getAuth0Instance();
        return client.loginWithRedirect<AppState>({
            appState: { targetUrl: window.location.href },
        });
    },

    async logout() {
        const client = await getAuth0Instance();
        return client.logout({
            logoutParams: { returnTo: window.location.origin },
        });
    },

    async handleRedirectCallback() {
        const client = await getAuth0Instance();
        return client.handleRedirectCallback<AppState>();
    },
};
